import {
	dispatchAcknowledgedEvent,
	openResourceSection,
} from '../../core/resourceEvents';
import {
	SWITCH_BULK_SECTION_EVENT,
	SWITCH_DASHBOARD_SECTION_EVENT,
	SWITCH_FILTER_EVENT,
	SWITCH_GLOBAL_VLANS_SECTION_EVENT,
	SWITCH_FOCUS_EVENT,
	SWITCH_HOME_SECTION_EVENT,
	SWITCH_SITE_SECTION_EVENT,
} from './events';
import {getResolvedSwitch} from './resolver';

const HOME_SECTIONS = new Set(['home.switches']);
const SITE_SECTIONS = new Set(['site.switches']);

/**
 * Open the Switch inventory on the Home Dashboard.
 *
 * One of five accordions there, beside Sites, SSIDs, Access Points and
 * Clients.
 *
 * @return {Promise<void>} completion acknowledgement
 */
export function openHomeSwitchSection() {
	return openResourceSection(
		SWITCH_HOME_SECTION_EVENT,
		'home.switches',
		HOME_SECTIONS,
	);
}

/**
 * Open the Switch inventory within one Site.
 *
 * Not an accordion on the Site Dashboard the way Access Points are - it is a
 * tab beside them that renders the whole Switch sub-page scoped to the site,
 * so the page has a tab to select before there is a table to speak to.
 *
 * @return {Promise<void>} completion acknowledgement
 */
export function openSiteSwitchSection() {
	return openResourceSection(
		SWITCH_SITE_SECTION_EVENT,
		'site.switches',
		SITE_SECTIONS,
	);
}

/**
 * Narrow the Switch inventory to a search term.
 * @param {string} query search text
 * @return {Promise<object>} acknowledgement
 */
export function applySwitchFilter(query) {
	const text = String(query || '').trim();
	return dispatchAcknowledgedEvent(SWITCH_FILTER_EVENT, {
		query: text,
		clear: text === '',
		resource: 'switch',
	});
}

/**
 * Point at one Switch row in the inventory.
 * @param {string} entityId serial number
 * @return {Promise<object>} acknowledgement
 */
export function focusSwitch(entityId) {
	if (!entityId) return Promise.reject(new Error('invalid_switch_focus'));
	if (!getResolvedSwitch(entityId)) {
		return Promise.reject(new Error('unresolved_switch_focus'));
	}
	return dispatchAcknowledgedEvent(SWITCH_FOCUS_EVENT, {
		entityId: String(entityId),
		resource: 'switch',
	});
}

const DASHBOARD_SECTIONS = new Set([
	'switch.ports',
	'switch.port_statistics',
	'switch.mac_table',
	'switch.alarms',
	'switch.lldp',
]);

/**
 * Open one accordion on a Switch Dashboard.
 *
 * Five sections share one event and one listener, keyed by the section id,
 * because they are five accordions on one page rather than five places.
 *
 * @param {string} sectionId semantic section identifier
 * @return {Promise<void>} completion acknowledgement
 */
export function openSwitchDashboardSection(sectionId) {
	return openResourceSection(
		SWITCH_DASHBOARD_SECTION_EVENT,
		sectionId,
		DASHBOARD_SECTIONS,
	);
}

const VLAN_SECTIONS = new Set(['site.global_vlans']);

/**
 * Open the Global VLAN list for a Site.
 *
 * A Site's, not one switch's: the list is shared by every switch in the
 * Site, which is why Canvas puts it beside the Switches table on the Site
 * Dashboard rather than on any switch's own page.
 *
 * @return {Promise<void>} completion acknowledgement
 */
export function openGlobalVlansSection() {
	return openResourceSection(
		SWITCH_GLOBAL_VLANS_SECTION_EVENT,
		'site.global_vlans',
		VLAN_SECTIONS,
	);
}

const BULK_SECTIONS = new Set([
	'maintenance.bulk_add_switches',
	'maintenance.bulk_delete_switches',
	'maintenance.bulk_switch_actions',
]);

/**
 * Open one of the bulk Switch screens.
 *
 * A fourth surface: bulk work lives on the Maintenance tab, not beside the
 * inventory it acts on, and each screen is chosen inside that page rather
 * than being a route of its own.
 *
 * @param {string} sectionId semantic section identifier
 * @return {Promise<void>} completion acknowledgement
 */
export function openSwitchBulkSection(sectionId) {
	return openResourceSection(
		SWITCH_BULK_SECTION_EVENT,
		sectionId,
		BULK_SECTIONS,
	);
}
